/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useServiceContext } from '../../../root'
import { useModal } from '../../../contexts/ModalContext'
import moment from 'moment-timezone'
import Modal from '../../../components/Modal'
import {
  ActivityDetailModalProps,
  ActivityLogDetail,
  LogDetailArray,
  LogDetailObject
} from './ActivityDetailModal.types'

export const ActivityDetailModal: React.FC<ActivityDetailModalProps> = ({ log }) => {
  const {
    service: {
      filter: { translate },
      init: initService
    }
  } = useServiceContext()
  const { hideModal } = useModal()
  const [show, setShow] = useState(false)

  // Parse and process detail data
  const processedDetail = React.useMemo(() => {
    let detail = log.detail

    // Parse as array if needed
    if (log.parseAsArray && Array.isArray(log.detail)) {
      detail = (log.detail as ActivityLogDetail[]).map((ob) => {
        const keys = Object.keys(ob)
        const key = keys[0]
        return [key, ob[key]]
      }) as LogDetailArray
    }

    // Remove expiration date if feature flag is disabled
    const featuresInfo = initService.getFeaturesInfo()
    if (
      featuresInfo &&
      !featuresInfo.isEnableDocDetailsExpirationDate &&
      log.action &&
      ['Document Created', 'Document Copied'].includes(log.action)
    ) {
      if (Array.isArray(detail)) {
        detail = (detail as ActivityLogDetail[]).map((obj) => parseObjectWithoutSystemExpiry(obj))
      } else if (typeof detail === 'object' && detail !== null) {
        detail = parseObjectWithoutSystemExpiry(detail as LogDetailObject)
      }
    }

    return detail
  }, [log.detail, log.parseAsArray, log.action, initService])

  // Check if detail[0] and detail[1] are empty
  const showEmpty1 =
    Array.isArray(processedDetail) &&
    processedDetail[0] !== undefined &&
    typeof processedDetail[0] === 'object' &&
    Object.keys(processedDetail[0]).length === 0

  const showEmpty2 =
    Array.isArray(processedDetail) &&
    processedDetail[1] !== undefined &&
    typeof processedDetail[1] === 'object' &&
    Object.keys(processedDetail[1]).length === 0

  useEffect(() => {
    // Delayed show for animation
    const timer = setTimeout(() => setShow(true), 300)
    return () => {
      clearTimeout(timer)
    }
  }, [])

  if (!show) return null

  return (
    <Modal show={show} handleClose={hideModal} title={translate('REPORTS_ACTIVITY_DETAIL_TITLE')} size="md">
      <Modal.Body>
        {/* Parent Folder */}
        {log.parentText && (
          <table className="file-details" style={{ marginBottom: 10 }}>
            <tbody>
              <tr>
                <td className="param" style={{ width: 240 }}>
                  {translate('REPORTS_ACTIVITY_DETAIL_PARENT_FOLDER')}
                </td>
                <td className="value word-break whitespaceprewrap">{log.parentText}</td>
              </tr>
            </tbody>
          </table>
        )}

        {/* Operating Mode User */}
        {log.type === 'OPERATING_MODE' && (
          <table className="file-details">
            <tbody>
              <tr>
                <td className="param">{translate('USER')}</td>
                <td className="value">{log.user?.chName}</td>
              </tr>
            </tbody>
          </table>
        )}

        {/* Folder Exported Documents */}
        {log.action === 'Folder Exported' && (
          <table className="file-details">
            <tbody>
              <tr>
                <td className="param" style={{ width: 240 }}>
                  {translate('FILE')}
                </td>
                <td className="value word-break whitespaceprewrap" style={{ paddingLeft: 15 }}>
                  {Array.isArray((processedDetail as LogDetailObject).Documents) &&
                    (processedDetail as LogDetailObject).Documents?.map((item, idx, arr) => (
                      <span key={idx}>
                        {item}
                        {idx < arr.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {/* Detail[2] if exists */}
        {Array.isArray(processedDetail) && processedDetail[2] && getSizeOfObject(processedDetail[2]) >= 0 && (
          <table className="file-details">
            <tbody>
              {Object.entries(processedDetail[2]).map(([key, value]) => (
                <tr key={key}>
                  <td className="param" style={{ width: 240 }}>
                    {key}
                  </td>
                  {!Array.isArray(value) && <td className="value word-break whitespaceprewrap">{String(value)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Main Detail Rendering */}
        {log.parseAsArray ? (
          <RenderArrayDetail detail={processedDetail as LogDetailArray} log={log} translate={translate} />
        ) : (
          <RenderObjectDetail
            detail={processedDetail as LogDetailObject}
            log={log}
            showEmpty1={showEmpty1}
            showEmpty2={showEmpty2}
            translate={translate}
          />
        )}
      </Modal.Body>

      <Modal.Footer>
        <button className="btn btn-primary" type="button" onClick={() => hideModal()}>
          {translate('OK')}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

// Helper Components
const RenderArrayDetail: React.FC<{
  detail: LogDetailArray
  log: any
  translate: (key: string) => string
}> = ({ detail, log, translate }) => {
  if (!detail || detail.length === 0) return null

  return (
    <table className="file-details">
      <tbody>
        {detail.map((entry, idx) => (
          <tr key={idx}>
            <td className="param" style={{ paddingLeft: 10, width: 240 }}>
              {entry[0]}
            </td>
            <td className="value word-break whitespaceprewrap">
              {entry[1]
                ? showIfDate(entry[1])
                  ? formatDate(postprocessTimeValue(entry[1]), 'shortDate')
                  : String(entry[1])
                : translate('NOT_AVAILABLE')}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const RenderObjectDetail: React.FC<{
  detail: LogDetailObject
  log: any
  showEmpty1: boolean
  showEmpty2: boolean
  translate: (key: string) => string
}> = ({ detail, log, showEmpty1, showEmpty2, translate }) => {
  const hasDetail0 = detail[0] && getSizeOfObject(detail[0]) >= 0
  const hasDetail1 = detail[1] && getSizeOfObject(detail[1]) >= 0
  const showBothLabels = hasDetail0 && hasDetail1

  const shouldShowAfterLabelOnly =
    ['Document Properties Created', 'Document Properties Reverted'].includes(log.action) && !showBothLabels

  return (
    <>
      {/* Before/After labels */}
      {showBothLabels && <label className="pl5 pb0">{translate('REPORTS_ACTIVITY_DETAIL_BEFORE')}</label>}
      {shouldShowAfterLabelOnly && <label className="pl5 pb0">{translate('REPORTS_ACTIVITY_DETAIL_AFTER')}</label>}

      {/* Detail[0] */}
      {detail[0] && getSizeOfObject(detail[0]) >= 0 && (
        <table className="file-details mb10">
          <tbody>
            {showEmpty1 && (
              <tr>
                <td colSpan={2} style={{ padding: '7px 10px' }}>
                  {translate('NOT_AVAILABLE')}
                </td>
              </tr>
            )}
            {Object.entries(detail[0]).map(([key, value]) => (
              <tr key={key}>
                <td className="param" style={{ paddingLeft: 10, width: 240 }}>
                  {key}
                </td>
                {!Array.isArray(value) &&
                  (['Property Created', 'Property Modified'].includes(log.action) ? (
                    <td className="value word-break whitespaceprewrap">
                      {value
                        ? showIfDate(value)
                          ? formatDate(postprocessTimeValue(value), 'shortDate')
                          : String(value)
                        : translate('NOT_AVAILABLE')}
                    </td>
                  ) : (
                    <td className="value word-break whitespaceprewrap">
                      {value
                        ? showIfDate(value)
                          ? `${formatDate(value, 'shortDate')} ${formatDate(value, 'mediumTime')}`
                          : String(value)
                        : translate('NOT_AVAILABLE')}
                    </td>
                  ))}
                {Array.isArray(value) && (
                  <td className="value word-break whitespaceprewrap">
                    <table style={{ width: '100%' }}>
                      <tbody>
                        {value.map((item, idx) => (
                          <tr key={idx}>
                            <td className="value word-break whitespaceprewrap" style={{ paddingBottom: 5 }}>
                              {Object.entries(item).map(([k, v]) => (
                                <span key={k}>
                                  {k}: {String(v)} <br />
                                </span>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* After label */}
      {showBothLabels && <label className="pl5 pb0">{translate('REPORTS_ACTIVITY_DETAIL_AFTER')}</label>}

      {/* Detail[1] */}
      {detail[1] && getSizeOfObject(detail[1]) >= 0 && (
        <table className="file-details">
          <tbody>
            {showEmpty2 && (
              <tr>
                <td colSpan={2} style={{ padding: '7px 10px' }}>
                  {translate('NOT_AVAILABLE')}
                </td>
              </tr>
            )}
            {Object.entries(detail[1]).map(([key, value]) => (
              <tr key={key}>
                <td className="param" style={{ width: 240, paddingLeft: 10 }}>
                  {key}
                </td>
                {!Array.isArray(value) &&
                  (log.action === 'Property Modified' ? (
                    <td className="value word-break whitespaceprewrap">
                      {value
                        ? showIfDate(value)
                          ? formatDate(postprocessTimeValue(value), 'shortDate')
                          : String(value)
                        : translate('NOT_AVAILABLE')}
                    </td>
                  ) : (
                    <td className="value word-break whitespaceprewrap">
                      {value
                        ? showIfDate(value)
                          ? `${formatDate(value, 'shortDate')} ${formatDate(value, 'mediumTime')}`
                          : String(value)
                        : translate('NOT_AVAILABLE')}
                    </td>
                  ))}
                {Array.isArray(value) && (
                  <td>
                    <table style={{ width: '100%' }}>
                      <tbody>
                        {value.map((item, idx) => (
                          <tr key={idx}>
                            <td className="value word-break whitespaceprewrap" style={{ paddingBottom: 5 }}>
                              {Object.entries(item).map(([k, v]) => (
                                <span key={k}>
                                  {k}: {String(v)} <br />
                                </span>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

// Helper Functions
function parseObjectWithoutSystemExpiry(obj: any): any {
  return 'Expiration Date' in obj
    ? Object.fromEntries(Object.entries(obj).filter(([key]) => key !== 'Expiration Date'))
    : obj
}

function showIfDate(data: any): boolean {
  const isnum = /^\d+$/.test(String(data))
  return isnum && String(data).length > 12
}

function postprocessTimeValue(value: any): number {
  const momentDate = moment.tz(parseInt(String(value), 10), 'GMT')
  return new Date(momentDate.year(), momentDate.month(), momentDate.date()).getTime()
}

function getSizeOfObject(obj: any): number {
  if (!obj || typeof obj !== 'object') return -1
  return Object.keys(obj).length
}

function formatDate(value: any, format: 'shortDate' | 'mediumTime'): string {
  const date = typeof value === 'number' ? new Date(value) : new Date(String(value))

  if (format === 'shortDate') {
    return date.toLocaleDateString()
  } else if (format === 'mediumTime') {
    return date.toLocaleTimeString()
  }
  return String(value)
}

export default ActivityDetailModal
